
import { useEffect, useRef, useState } from "react";
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver";

interface VideoPlayerProps {
  videoSrc: string;
  posterSrc: string;
  altText: string;
  className?: string;
}

const VideoPlayer = ({ videoSrc, posterSrc, altText, className = "" }: VideoPlayerProps) => {
  // Use intersection observer to detect when video is visible
  const [videoWrapperRef, isVideoVisible] = useIntersectionObserver({
    threshold: 0.15,
    triggerOnce: false,
    resetOnEachView: true
  });
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  
  // Debug logging for visibility changes
  useEffect(() => {
    console.log(`Video ${videoSrc} isVisible:`, isVideoVisible);
  }, [isVideoVisible, videoSrc]);
  
  // Handle video playback when component becomes visible
  useEffect(() => {
    if (isVideoVisible && videoRef.current && !videoError) {
      console.log(`Attempting to play video: ${videoSrc}`);
      videoRef.current.play().catch(error => {
        console.error(`Error playing video ${videoSrc}:`, error);
        setVideoError(true);
      });
    } else if (!isVideoVisible && videoRef.current) {
      console.log(`Pausing video: ${videoSrc}`);
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isVideoVisible, videoError, videoSrc]);

  return (
    <div 
      ref={videoWrapperRef} 
      className={`aspect-square bg-[#FAF9F8] rounded-xl overflow-hidden relative ${className}`}
    >
      {!videoError ? (
        <video 
          ref={videoRef}
          className="w-full h-full object-cover rounded-xl"
          src={videoSrc}
          poster={posterSrc}
          muted
          playsInline
          preload="auto"
          onLoadedData={() => console.log(`Video loaded: ${videoSrc}`)}
          onError={() => {
            console.log(`Video failed to load: ${videoSrc}, falling back to poster`);
            setVideoError(true);
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <img 
            src={posterSrc}
            alt={altText} 
            className="w-full h-full object-cover rounded-xl"
          />
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
